import { Check } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useSubscription, useSubscriptionTiers, useCreateCheckout, type TierInfo } from '@/api'

function formatPrice(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`
}

function formatTimeSaved(emailLimit: number): string {
  // Estimate: 50% of emails save 30 seconds = average 15 seconds per email
  const secondsSaved = emailLimit * 15
  const hoursSaved = Math.round(secondsSaved / 3600)
  return `${hoursSaved} hours`
}

type PricingCardProps = {
  tier: TierInfo
  currentTier: string | null
  isPopular?: boolean
  onSubscribe: (tier: 'SOLO' | 'PRO' | 'BUSINESS') => void
  isLoading: boolean
}

function PricingCard({ tier, currentTier, isPopular, onSubscribe, isLoading }: PricingCardProps) {
  const isCurrent = currentTier === tier.tier
  const isUpgrade = currentTier && ['SOLO', 'PRO', 'BUSINESS'].indexOf(tier.tier) > ['SOLO', 'PRO', 'BUSINESS'].indexOf(currentTier)
  const isDowngrade = currentTier && ['SOLO', 'PRO', 'BUSINESS'].indexOf(tier.tier) < ['SOLO', 'PRO', 'BUSINESS'].indexOf(currentTier)

  return (
    <Card className={`relative flex flex-col ${isPopular ? 'border-primary shadow-lg scale-105' : ''}`}>
      {isPopular && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2" variant="default">
          Most Popular
        </Badge>
      )}
      {isCurrent && (
        <Badge className="absolute -top-3 left-1/2 -translate-x-1/2" variant="secondary">
          Current Plan
        </Badge>
      )}
      <CardHeader className="text-center pb-2">
        <CardTitle className="text-2xl">{tier.name}</CardTitle>
        <CardDescription className="text-muted-foreground">
          {tier.tier === 'SOLO' && 'Perfect for personal use'}
          {tier.tier === 'PRO' && 'For professionals with multiple inboxes'}
          {tier.tier === 'BUSINESS' && 'For teams and power users'}
        </CardDescription>
        {tier.tier === 'SOLO' && (
          <div className="mt-2">
            <Badge variant="default" className="text-xs">
              30-day free trial
            </Badge>
          </div>
        )}
      </CardHeader>
      <CardContent className="flex-1">
        <div className="text-center mb-6">
          <span className="text-4xl font-bold">{formatPrice(tier.price_cents)}</span>
          <span className="text-muted-foreground">/month</span>
        </div>
        <div className="space-y-2 mb-6">
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">{tier.email_limit.toLocaleString()}</span>
            <span className="text-muted-foreground">emails per month</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">{tier.inbox_limit}</span>
            <span className="text-muted-foreground">connected {tier.inbox_limit === 1 ? 'inbox' : 'inboxes'}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="font-medium">up to {formatTimeSaved(tier.email_limit)}</span>
            <span className="text-muted-foreground">saved every month</span>
          </div>
        </div>
        <ul className="space-y-2">
          {tier.features.map((feature, index) => (
            <li key={index} className="flex items-start gap-2 text-sm">
              <Check className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </CardContent>
      <CardFooter>
        <Button
          className="w-full"
          variant={isCurrent ? 'outline' : isPopular ? 'default' : 'secondary'}
          disabled={isCurrent || isLoading}
          onClick={() => onSubscribe(tier.tier)}
        >
          {isLoading ? 'Loading...' : isCurrent ? 'Current Plan' : isUpgrade ? 'Upgrade' : isDowngrade ? 'Downgrade' : 'Subscribe'}
        </Button>
      </CardFooter>
    </Card>
  )
}

function PricingCardSkeleton() {
  return (
    <Card className="flex flex-col">
      <CardHeader className="text-center pb-2">
        <Skeleton className="h-8 w-24 mx-auto mb-2" />
        <Skeleton className="h-4 w-48 mx-auto" />
      </CardHeader>
      <CardContent className="flex-1">
        <div className="text-center mb-6">
          <Skeleton className="h-10 w-20 mx-auto" />
        </div>
        <div className="space-y-2 mb-6">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-full" />
        </div>
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-4 w-full" />
          ))}
        </div>
      </CardContent>
      <CardFooter>
        <Skeleton className="h-10 w-full" />
      </CardFooter>
    </Card>
  )
}

export function PricingPage() {
  const { data: subscription, isLoading: subscriptionLoading } = useSubscription()
  const { data: tiers, isLoading: tiersLoading } = useSubscriptionTiers()
  const createCheckout = useCreateCheckout()

  const currentTier = subscription?.subscription?.tier ?? null

  const handleSubscribe = async (tier: 'SOLO' | 'PRO' | 'BUSINESS') => {
    try {
      const result = await createCheckout.mutateAsync({
        tier,
        successUrl: `${window.location.origin}/settings?subscription=success`,
        cancelUrl: `${window.location.origin}/pricing?subscription=cancelled`,
      })
      // Redirect to Stripe Checkout
      window.location.href = result.checkout_url
    } catch (error) {
      console.error('Failed to create checkout session:', error)
    }
  }

  const isLoading = subscriptionLoading || tiersLoading

  return (
    <div className="space-y-8 max-w-6xl mx-auto">
      <div className="text-center">
        <h1 className="text-3xl font-bold tracking-tight lg:text-4xl">Choose Your Plan</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl mx-auto">
          Select the plan that best fits your email automation needs. All plans include AI-powered email processing and custom instructions.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-3 items-stretch">
        {isLoading ? (
          <>
            <PricingCardSkeleton />
            <PricingCardSkeleton />
            <PricingCardSkeleton />
          </>
        ) : (
          tiers?.map((tier) => (
            <PricingCard
              key={tier.tier}
              tier={tier}
              currentTier={currentTier}
              isPopular={tier.tier === 'PRO'}
              onSubscribe={handleSubscribe}
              isLoading={createCheckout.isPending}
            />
          ))
        )}
      </div>

      <div className="text-center text-sm text-muted-foreground">
        <p>Solo includes a 30-day free trial. Cancel anytime.</p>
        <p className="mt-1">
          Need more? Contact us at{' '}
          {/* eslint-disable-next-line no-restricted-syntax -- mailto: links are acceptable */}
          <a href="mailto:support@example.com" className="text-primary hover:underline">
            support@example.com
          </a>
        </p>
      </div>
    </div>
  )
}
